/**
 * Turning the one name on a scanned sheet into a bowler.
 *
 * It is the only thing on the paper that identifies anyone -- there is no
 * customer number and often no phone -- so the matching here is deliberately
 * timid. An exact match opens that bowler; anything less certain creates a new
 * one. Merging two people's specs is far worse than a duplicate somebody can
 * delete in two clicks.
 */

const normalise = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z ]/g, "")
    .replace(/\s+/g, " ")
    .trim();

/**
 * "Branden Deremer" -> first "Branden", last "Deremer".
 *
 * The last word is the surname and everything before it is the given name, so
 * a middle name stays where it belongs instead of being dropped.
 */
export const splitScannedName = (full) => {
  const parts = String(full || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return { firstname: "", lastname: "" };
  if (parts.length === 1) return { firstname: parts[0], lastname: "" };
  return {
    firstname: parts.slice(0, -1).join(" "),
    lastname: parts[parts.length - 1],
  };
};

/**
 * The one bowler whose name matches exactly, or null.
 *
 * Searches only the bowlers already loaded, which is a real limit: the list is
 * paged, so a sheet for someone further down it will not be found and a
 * duplicate will be created instead.
 */
export const findBowler = (customers, scannedName) => {
  const target = normalise(scannedName);
  if (!target) return null;
  return (
    (customers || []).find(
      (c) => normalise(`${c.firstname} ${c.lastname}`) === target,
    ) || null
  );
};

/**
 * Levenshtein distance, capped.
 *
 * Small on purpose: the only question asked of it is whether one handwritten
 * surname is a letter or two away from another, and a full library for that
 * would be a dependency to carry for one loop.
 */
const distance = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++) {
      row[j] = Math.min(
        prev[j] + 1,
        row[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    prev = row;
  }
  return prev[b.length];
};

const digits = (value) => String(value || "").replace(/\D+/g, "");

export const MATCH_PHONE = "phone";
export const MATCH_EXACT = "exact";
export const MATCH_CLOSE = "close";
export const MATCH_WEAK = "weak";

/**
 * How likely one bowler is to be the person on the sheet.
 *
 * A phone number outranks a name and always will: it is machine-written on
 * most sheets and unique in practice, where a handwritten surname is neither.
 * Then an exact name, then a name a letter or two out -- which is what the
 * reader produces from a smudged sheet, and the case that was quietly making
 * duplicates.
 */
export const scoreCandidate = (customer, scanned) => {
  const phone = digits(scanned.phone);
  if (phone && phone.length >= 7 && digits(customer.telephone) === phone) {
    return { rank: 0, reason: MATCH_PHONE };
  }

  const theirs = normalise(`${customer.firstname} ${customer.lastname}`);
  const ours = normalise(scanned.name);
  if (!ours || !theirs) return { rank: 4, reason: MATCH_WEAK };
  if (theirs === ours) return { rank: 1, reason: MATCH_EXACT };

  // Compared on the surname alone: given names get abbreviated and
  // nicknamed on paper far more often than surnames do.
  const theirLast = normalise(customer.lastname);
  const ourLast = normalise(splitScannedName(scanned.name).lastname);
  if (theirLast && ourLast) {
    const d = distance(theirLast, ourLast);
    // One letter for a short surname, two once there is enough of it that two
    // wrong letters is still clearly the same word.
    const allowed = ourLast.length >= 6 ? 2 : 1;
    if (d <= allowed) return { rank: 2, reason: MATCH_CLOSE };
  }

  return { rank: 3, reason: MATCH_WEAK };
};

/**
 * Candidates ordered by how likely each is, with the reason kept so the
 * operator can see why a row is being suggested.
 */
export const rankCandidates = (customers, scanned) =>
  (customers || [])
    .map((c) => ({ ...c, ...scoreCandidate(c, scanned) }))
    .sort((a, b) => a.rank - b.rank);

/**
 * The one candidate confident enough to preselect, or "".
 *
 * Only a phone match or an exact name, and only when nothing else shares that
 * standing. A close-but-not-exact name is shown and never chosen: it is
 * exactly as likely to be this person's brother.
 */
export const confidentMatch = (ranked) => {
  const best = (ranked || [])[0];
  if (!best || best.rank > 1) return "";
  const tied = ranked.filter((c) => c.rank === best.rank);
  return tied.length === 1 ? best.id : "";
};
